import { v4 as uuid } from 'uuid';

import {
  ModuleTypes,
  TransactionModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { generateModuleToken } from 'dashboard/models/case-template';
import { Configuration } from 'dashboard/models/grid-layout';
import { TransactionModule } from 'dashboard/models/transaction-type-grid-layout';
import { ResourceInquiryAttribute } from 'dashboard/modules/ResourceInquiry';
import { GovernmentIdVerificationModuleAttribute } from 'dashboard/modules/ResourceVerification/model';
import { ApiModel } from 'lib/interfaces/api';

export const generateDefaultTransactionModuleConfiguration = (
  moduleType: TransactionModuleTypes,
): TransactionModule => {
  switch (moduleType) {
    case ModuleTypes.Markdown:
      return {
        type: ModuleTypes.Markdown,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 30 },
        'module-layout': {
          'dynamic-height': false,
        },
        markdown: '# Heading 1 \n ## Heading 2 \n ### Heading 3 \n - bulleted \n - list',
      };
    case ModuleTypes.Inquiryv2:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        'variable-token': 'inquiries',
        position: { x: 0, y: 60, width: 8, height: 52 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'inquiries',
        'default-accordion-open': true,
        'view-layout': 'list',
        attributes: [
          ResourceInquiryAttribute.Photos,
          ResourceInquiryAttribute.Name,
          ResourceInquiryAttribute.GovernmentIDNumber,
          ResourceInquiryAttribute.BirthDate,
          ResourceInquiryAttribute.Address,
          ResourceInquiryAttribute.ExpirationDate,
          ResourceInquiryAttribute.EmailAddress,
          ResourceInquiryAttribute.PhoneNumber,
          ResourceInquiryAttribute.SessionLocation,
          ResourceInquiryAttribute.Fields,
          ResourceInquiryAttribute.DocumentExtraction,
        ],
      };
    case ModuleTypes.DataDisplay:
      return {
        type: ModuleTypes.DataDisplay,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 60 },
        'module-layout': {
          'dynamic-height': true,
        },
        title: 'Sample Data',
        sections: [
          {
            title: 'Section A',
            attributes: [
              {
                label: 'Attribute 1',
                value: 'Attribute Value',
              },
              {
                label: 'Attribute 2',
                value: 'Attribute Value',
                obscurable: true,
              },
            ],
          },
        ],
        'collapse-empty-fields': false,
      };
    case ModuleTypes.Verificationv2:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        'variable-token': 'verifications',
        position: { x: 0, y: 0, width: 8, height: 102 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'verifications',
        'default-accordion-open': true,
        'view-layout': 'list',
        'verification-type': ApiModel.VerificationGovernmentId,
        'verification-templates': [],
        attributes: [
          GovernmentIdVerificationModuleAttribute.Photos,
          GovernmentIdVerificationModuleAttribute.Id,
          GovernmentIdVerificationModuleAttribute.FullName,
          GovernmentIdVerificationModuleAttribute.BirthDate,
          GovernmentIdVerificationModuleAttribute.IssuingCountry,
          GovernmentIdVerificationModuleAttribute.IssueDate,
          GovernmentIdVerificationModuleAttribute.ExpirationDate,
          GovernmentIdVerificationModuleAttribute.CaptureMethod,
          GovernmentIdVerificationModuleAttribute.CaptureMethodBack,
          GovernmentIdVerificationModuleAttribute.Address,
          GovernmentIdVerificationModuleAttribute.Nationality,
          GovernmentIdVerificationModuleAttribute.Checks,
        ],
      };
    case ModuleTypes.Reportv2:
      return {
        type: ModuleTypes.Resource,
        token: generateModuleToken(),
        'variable-token': 'reports',
        position: { x: 0, y: 0, width: 8, height: 78 },
        'module-layout': {
          'dynamic-height': true,
        },
        'field-key': 'reports',
        'default-accordion-open': true,
        'view-layout': 'list',
        'report-template-type': ApiModel.ReportTemplateWatchlist,
        attributes: [],
      };
    case ModuleTypes.Relations:
      return {
        type: ModuleTypes.Relations,
        token: generateModuleToken(),
        position: { x: 0, y: 112, width: 8, height: 40 },
        'module-layout': {
          'dynamic-height': false,
        },
      };
    case ModuleTypes.Tags:
      return {
        type: ModuleTypes.Tags,
        token: generateModuleToken(),
        position: { x: 8, y: 0, width: 4, height: 18 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.EventTimeline:
      return {
        type: ModuleTypes.EventTimeline,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 4, height: 70 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
    case ModuleTypes.ListMatches:
      return {
        type: ModuleTypes.ListMatches,
        token: generateModuleToken(),
        position: { x: 0, y: 0, width: 8, height: 50 },
        'module-layout': {
          'dynamic-height': true,
        },
      };
  }
};

export const generateDefaultTransactionTypeGridLayoutConfiguration =
  (): Configuration<TransactionModule> => ({
    tabs: [
      {
        name: 'Profile',
        token: uuid(),
        modules: [
          generateDefaultTransactionModuleConfiguration(ModuleTypes.DataDisplay),
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Inquiryv2),
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Tags),
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Relations),
        ],
      },
      {
        name: 'Reports',
        token: uuid(),
        modules: [
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Reportv2),
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Tags),
        ],
      },
      {
        name: 'Verifications',
        token: uuid(),
        modules: [
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Verificationv2),
          generateDefaultTransactionModuleConfiguration(ModuleTypes.Tags),
        ],
      },
    ],
  });
